import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sun, Zap, Shield, TrendingUp, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SEO from '../components/SEO';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Energy Spectrum",
    "image": "https://customer-assets.emergentagent.com/job_energy-unlimited/artifacts/mjwptr25_ENERGY_no%20Buffer.png",
    "@id": "",
    "url": "",
    "telephone": "0404844257",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Leppington",
      "addressLocality": "Sydney",
      "addressRegion": "NSW",
      "postalCode": "2179",
      "addressCountry": "AU"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": -33.9616,
      "longitude": 150.8065
    },
    "openingHoursSpecification": [{
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "18:00"
    }, {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": "Saturday",
      "opens": "09:00",
      "closes": "16:00"
    }],
    "sameAs": [
      "https://www.facebook.com/energyspectrum",
      "https://www.instagram.com/energyspectrum",
      "https://www.linkedin.com/company/energyspectrum"
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "127"
    },
    "description": "Leading solar panel and battery installation company in Sydney, NSW. Residential & commercial solar systems, Sigenergy, Fox ESS, Goodwe batteries."
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    {
      title: 'Residential Solar',
      description: 'Transform your home with clean, renewable energy. Our residential solar solutions help you reduce electricity bills while contributing to a sustainable future.',
      image: 'https://images.unsplash.com/photo-1756542713155-94f62d47d1b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxyZXNpZGVudGlhbCUyMHNvbGFyJTIwcGFuZWxzJTIwcm9vZnRvcHxlbnwwfHx8fDE3NjAyMjg1MDV8MA&ixlib=rb-4.1.0&q=85',
      icon: Sun
    },
    {
      title: 'Commercial Solar',
      description: 'Power your business with sustainable energy solutions. Our commercial solar systems are designed to maximize efficiency and reduce operational costs.',
      image: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwc29sYXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzYwMjI4NTEwfDA&ixlib=rb-4.1.0&q=85',
      icon: Zap
    },
    {
      title: 'Solar Maintenance',
      description: 'Keep your solar system running at peak performance. Our professional cleaning and maintenance services ensure maximum energy output year-round.',
      image: 'https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHBhbmVsJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzYwNjY3MTI1fDA&ixlib=rb-4.1.0&q=85',
      icon: Shield
    }
  ];

  const stats = [
    { value: '12+', label: 'Years Industry Experience' },
    { value: '100%', label: 'Quality Commitment' },
    { value: 'NSW', label: 'Proudly Local' },
    { value: '24/7', label: 'Support Available' }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Reduce Energy Bills',
      description: 'Save up to 80% on your electricity costs with our efficient solar solutions'
    },
    {
      icon: Shield,
      title: 'Quality Guarantee',
      description: 'Industry-leading warranties and professional installation backed by certified experts'
    },
    {
      icon: Zap,
      title: 'Clean Energy',
      description: 'Reduce your carbon footprint and contribute to a sustainable future'
    },
    {
      icon: Sun,
      title: 'Energy Independence',
      description: 'Take control of your energy future with reliable solar power and battery storage'
    }
  ];

  return (
    <>
      <SEO 
        title="Solar Panel Installation Sydney | Home Battery Storage NSW"
        description="Leading solar panel and battery installation Sydney. 12 years experience. Residential & commercial solar systems. Sigenergy, Fox ESS, Goodwe batteries. Call 0404 844 257"
        keywords="solar panel installation Sydney, solar and battery system Sydney, solar company Sydney, home battery storage Sydney, solar installer Sydney NSW"
        schemaMarkup={schemaMarkup}
      />
      <div className="home-container">
        {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className={`hero-text ${isVisible ? 'fadeIn' : ''}`}>
            <h1 className="display-large">
              Clean Energy <br />
              <span className="highlight-text">Without Limits</span>
            </h1>
            <p className="body-large hero-description">
              Power your home or business with sustainable solar energy. 
              A new company backed by 12 years of solar industry expertise.
            </p>
            <div className="hero-buttons">
              <Link to="/contact">
                <Button className="btn-cta">Get Free Quote</Button>
              </Link>
              <Link to="/services">
                <Button className="btn-secondary">Our Services</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className={`stat-item ${isVisible ? 'zoomIn' : ''}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Services</h2>
            <p className="body-large section-description">
              Comprehensive solar solutions tailored to your needs
            </p>
          </div>
          <div className="services-grid">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card 
                  key={index} 
                  className="service-card"
                >
                  <div className="service-image-container">
                    <img 
                      src={service.image} 
                      alt={`${service.title} - Energy Spectrum solar installation Sydney`}
                      className="service-image"
                    />
                  </div>
                  <div className="service-content">
                    <div className="service-icon">
                      <IconComponent size={32} />
                    </div>
                    <h3 className="heading-3">{service.title}</h3>
                    <p className="body-medium">{service.description}</p>
                  </div>
                </Card>
              );
            })}
          </div>
          <div className="section-cta">
            <Link to="/services">
              <Button className="btn-primary">View All Services</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Why Choose Energy Spectrum?</h2>
            <p className="body-large section-description">
              Experience the benefits of partnering with experienced solar professionals
            </p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div key={index} className="benefit-card">
                  <div className="benefit-icon">
                    <IconComponent size={40} />
                  </div>
                  <h3 className="heading-3">{benefit.title}</h3>
                  <p className="body-medium">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Battery Rebate CTA Section */}
      <section className="rebate-cta-section">
        <div className="container">
          <div className="rebate-cta-card">
            <div className="rebate-cta-icon">
              <Zap size={48} />
            </div>
            <div className="rebate-cta-content">
              <h2 className="heading-2">Claim Your Federal Battery Rebate</h2>
              <p className="body-large">
                Take advantage of Australia's home battery rebate program and save thousands on your energy storage system. 
                Limited time offer - act now to secure your rebate!
              </p>
            </div>
            <div className="rebate-cta-action">
              <Link to="/contact">
                <Button className="btn-cta">Claim Rebate Now</Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-1">Ready to Go Solar?</h2>
            <p className="body-large cta-description">
              Start your clean energy journey with a team that brings 12 years of solar expertise
            </p>
            <div className="cta-buttons">
              <Link to="/contact">
                <Button className="btn-cta">Get Started Today</Button>
              </Link>
              <a href="tel:0404844257">
                <Button className="btn-secondary">
                  <Phone size={20} style={{ marginRight: '8px' }} />
                  Call Us Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default Home;